
import { defineComponent, reactive, Ref, ref, toRefs } from 'vue'
import AButton from '@/components/Forms/AButton.vue'
import useCurrentUser from '@/composables/user/user'
import usePostApi from '@/composables/post/postApi'
import { Post } from '@/model/data-object/Post'
import usePostFactory from '@/composables/post/postFactory'
import { NewPost } from '@/model/data-object/NewPost'
import validatePost, {
  PostContentConstraints,
} from '@/validation/postValidator'
import ValidationError from '@/error/validationError'
import ApiValidationError from '@/error/apiValidationError'
import ACheckbox from '@/components/Forms/ACheckbox.vue'
import { TopicNotification } from '@/model/data-object/TopicNotification'
import useConfiguration from '@/composables/configuration/configuration'
import ApiError from '@/error/apiError'
import useAppState from '@/composables/ui/ui'
import { TopicStatus } from '@/model/data-object/TopicSettings'
import useTopic from '@/composables/topic/topic'
import PostText from '@/components/Forms/PostText.vue'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_NEW, ANALYTICS_EVENT_POST_RESPOND,
} from '@/composables/analytics/analyticsManager'
import useUiFactory from '@/composables/ui/uiFactory'
import usePostsLoader from '@/composables/post/postLoader'

export default defineComponent({
  name: 'PostCreate',
  emits: ['created-post'],
  components: { PostText, ACheckbox, AButton },
  props: {
    parentPost: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    defaultTitle: {
      type: Boolean,
      default: true,
    },
    allowClose: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      CONTENT_MAX_LENGTH: PostContentConstraints.MAX_LENGTH,
      TOPIC_STATUS_BLOCKED: TopicStatus.BLOCKED,
      TOPIC_STATUS_ACTIVE: TopicStatus.ACTIVE,
      TOPIC_STATUS_DELETED: TopicStatus.DELETED,
    }
  },
  setup(props, context) {
    const postContentRef = ref(null)
    const { configuration } = useConfiguration()
    const { currentUser } = useCurrentUser()
    const { initPost } = usePostFactory()
    const { lastSavedPost, postIdToShow } = useAppState()
    const newPost: Ref<NewPost | undefined> = ref<NewPost>(
      initPost(props.parentPost as Post)
    )
    const postState = reactive({
      savingPost: false,
    })
    const validationError: Ref<ValidationError | undefined> = ref()
    const apiError: Ref<ApiError | undefined> = ref()
    const subscribeTopic: Ref<boolean | undefined> = ref(false)
    const subscribePost: Ref<boolean | undefined> = ref(false)
    const { topic } = useTopic()
    const { removeRespondHash } = useUiFactory()
    const { addPost } = usePostsLoader()

    const createPost = () => {
      if (subscribeTopic.value) {
        newPost.value?.notifications.push(
          new TopicNotification(
            props.parentPost
              ? props.parentPost.topic.id
              : configuration.widget.topicId
          )
        )
      }

      try {
        validatePost(newPost.value as NewPost)
        const { create } = usePostApi()
        postState.savingPost = true
        create(newPost.value as NewPost)
          .then((post: Post) => {
            const { propagateAnalyticsEvent } = useAnalyticsManager()
            propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_NEW)
            lastSavedPost.value = post
            postIdToShow.value = null
            context.emit('created-post', post)
            newPost.value = initPost(props.parentPost as Post)
            removeRespondHash()

            if (post.parent) {
              addPost(post)
            }
          })
          .catch((err: Error) => {
            if (err instanceof ApiValidationError) {
              validationError.value = err
            } else if (err instanceof ApiError) {
              apiError.value = err
            }
          })
          .finally(() => {
            postState.savingPost = false
            validationError.value = undefined
          })
      } catch (err) {
        validationError.value = err
      }
    }

    return {
      topic,
      postContentRef,
      currentUser,
      newPost,
      subscribeTopic,
      subscribePost,
      validationError,
      apiError,
      ...toRefs(postState),
      createPost,
    }
  },
})
